import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetchWithRecovery } from '../../utils/sessionRecovery';

export interface ResourceError {
  /** HTTP status, or 0 when the request never got an answer. */
  status: number;
  message: string;
}

export interface AdminResource<T> {
  data: T | null;
  error: ResourceError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * GET one `/api/admin` resource for a directory page.
 *
 * Goes through `authFetchWithRecovery`, so an admin token that aged out (they
 * live 15 minutes) is renewed from the Central session instead of the page
 * failing. A null response means the browser is already on its way to sign-in:
 * the page stays in its loading state and sets nothing.
 *
 * Only the newest request may set state, so a slow answer to an old search
 * cannot replace the results of a newer one.
 */
export function useAdminResource<T>(path: string): AdminResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ResourceError | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const loadedPath = useRef<string | null>(null);

  useEffect(() => {
    let current = true;
    // A reload of the page already shown keeps it on screen while it refreshes
    // (so an open dialog is not unmounted under the admin); a new path loads.
    if (loadedPath.current !== path) setLoading(true);
    setError(null);

    (async () => {
      try {
        const { response } = await authFetchWithRecovery(`/api/admin${path}`);
        if (!current || response === null) return;
        const body = (await response.json().catch(() => null)) as
          | (T & { error?: string })
          | null;
        if (!current) return;
        if (!response.ok || body === null) {
          setData(null);
          setError({
            status: response.status,
            message: body && typeof body.error === 'string' ? body.error : `Request failed (${response.status})`,
          });
        } else {
          setData(body);
          loadedPath.current = path;
        }
        setLoading(false);
      } catch {
        if (!current) return;
        setData(null);
        setError({ status: 0, message: 'Could not reach Central. Check your connection and try again.' });
        setLoading(false);
      }
    })();

    return () => {
      current = false;
    };
  }, [path, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { data, error, loading, reload };
}
